import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";
import { PageHero, SectionEyebrow } from "@/components/Primitives";
import { ArrowRight, CheckCircle2, Loader2, XCircle } from "lucide-react";
import SEO from "@/lib/seo";

const MAX_ATTEMPTS = 6;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error | none
  const [detail, setDetail] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("none"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setDetail(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page">
      <SEO title="Membership · Checkout · The Cleanability Institute" path="/checkout/success" />
      <PageHero
        testId="checkout-success-hero"
        eyebrow="Checkout"
        title="Activating your membership."
        lead="We are verifying your payment with Stripe. This usually completes in a few seconds."
      />
      <section className="container-x pb-24">
        <div className="rounded-3xl border border-border bg-card p-10 md:p-16 max-w-3xl">
          {status === "polling" && (
            <div data-testid="checkout-status-polling" className="flex items-center gap-4">
              <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" />
              <div>
                <div className="font-display text-xl font-medium">Confirming payment…</div>
                <div className="text-sm text-muted-foreground mt-1">Hold tight. We are checking the session with Stripe.</div>
              </div>
            </div>
          )}
          {status === "paid" && (
            <div data-testid="checkout-status-paid">
              <CheckCircle2 className="h-8 w-8 text-[hsl(var(--accent))]" />
              <div className="mt-6 font-display text-3xl md:text-4xl font-medium tracking-tight">Membership activated.</div>
              <p className="mt-3 text-muted-foreground">Thank you. Your access to the Institute is open. Welcome.</p>
              <Link to="/dashboard" data-testid="checkout-to-dashboard" className="mt-8 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90">
                Open dashboard <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
          {(status === "expired" || status === "error" || status === "none") && (
            <div data-testid="checkout-status-error">
              <XCircle className="h-8 w-8 text-destructive" />
              <div className="mt-6 font-display text-3xl md:text-4xl font-medium tracking-tight">Payment not confirmed.</div>
              <p className="mt-3 text-muted-foreground">
                {status === "none" ? "Missing session reference." : "We could not verify the payment automatically. If you completed checkout, please refresh in a moment, or contact us."}
              </p>
              <Link to="/membership" data-testid="checkout-back-to-membership" className="mt-8 inline-flex items-center gap-2 h-11 px-5 rounded-md border border-border text-sm font-medium hover:border-[hsl(var(--accent))]">
                Back to membership
              </Link>
            </div>
          )}
          {detail && (
            <div className="mt-10 pt-6 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div><div className="text-muted-foreground">Session</div><div className="font-mono-mini mt-1 truncate">{sessionId}</div></div>
              <div><div className="text-muted-foreground">Payment status</div><div className="font-mono-mini mt-1">{detail.payment_status}</div></div>
              <div><div className="text-muted-foreground">Amount</div><div className="font-mono-mini mt-1">{(detail.amount_total / 100).toFixed(2)} {detail.currency?.toUpperCase()}</div></div>
              <div><div className="text-muted-foreground">Status</div><div className="font-mono-mini mt-1">{detail.status}</div></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
